import { createFileRoute } from '@tanstack/react-router';
import { ErrorSummary } from '@osd/ui/error-summary';
import { Stack } from '@osd/ui/stack';
import { Text } from '@osd/ui/text';
import { CodeBlock } from '@osd/ui/code-block';
import { Section } from '@osd/ui/section';

/**
 * /ui/error-summary — live docs for the `ErrorSummary` primitive.
 */
export const Route = createFileRoute('/ui/_layout/error-summary')({
  component: ErrorSummaryPage,
});

function ErrorSummaryPage() {
  return (
    <Stack gap={6}>
      <Section title="ErrorSummary" hint="GOV.UK-style top-of-form error list">
        <ErrorSummary
          errors={[
            {
              fieldId: 'email',
              fieldLabel: 'Email',
              message: 'Enter a valid email.',
            },
            {
              fieldId: 'password',
              fieldLabel: 'Password',
              message: 'At least 12 characters.',
            },
          ]}
        />
      </Section>

      <Section
        title="Empty state"
        hint="renders nothing when errors is empty — safe to always render"
      >
        <Stack gap={4}>
          <ErrorSummary
            errors={[{ fieldId: 'username', fieldLabel: 'Username', message: 'Already taken.' }]}
            intro="Choose a different name and try again."
          />
          <ErrorSummary errors={[]} />
          <Text muted>
            ↑ Renders nothing — the empty list collapses to <code>null</code> so consumers can
            always render the summary without a conditional.
          </Text>
        </Stack>
      </Section>

      <Section title="Code example" hint="copy-pasteable usage">
        <Stack gap={5}>
          <CodeBlock
            title="Minimal — one error"
            language="tsx"
            code={`<ErrorSummary
  errors={[
    { fieldId: 'email', fieldLabel: 'Email', message: 'Enter a valid email.' },
  ]}
/>`}
          />

          <CodeBlock
            title="From useForm errors"
            hint="Convert the form.errors object to ErrorSummary items"
            language="tsx"
            code={`const items = Object.entries(form.errors).map(([fieldId, message]) => ({
  fieldId,
  fieldLabel: fieldId,
  message: String(message),
}));
return items.length > 0 ? <ErrorSummary errors={items} /> : null;`}
          />

          <CodeBlock
            title="Always render (no conditional)"
            hint="errors=[] renders null — drop the guard and pass form.errors directly"
            language="tsx"
            code={`<ErrorSummary errors={form.errors} intro="Fix the highlighted fields below." />`}
          />

          <CodeBlock
            title="Custom title + heading level"
            language="tsx"
            code={`<ErrorSummary
  title="3 things need your attention"
  titleAs={3}
  errors={items}
/>`}
          />
        </Stack>
      </Section>
    </Stack>
  );
}
